import { get } from 'lodash'

import { CANDIDATE_DETAILS, DISBURSEMENTS } from '../hardcodedQuestions'

import { escapeCsvValue } from './escapeCsvValue'
import { BuildReportCsvParams, CsvColumn, CandidateReportRow } from './useGenerateReport.types'

type Submission = CandidateReportRow['submissions'][number]
type TaskTemplate = NonNullable<BuildReportCsvParams['taskTemplates']>[number]
type ReportQuestion = { id: string }
type QuestionLabeller = (questionId: string) => string

const getTemplateSubmissions = (row: CandidateReportRow, templateId: string): Submission[] =>
  row.submissions.filter(submission => submission.taskTemplateId === templateId)

const answerValue = (submission: Submission | undefined, questionId: string): string =>
  submission?.answers.find(answer => answer.questionId === questionId)?.value ?? ''

const buildSingleSubmissionColumns = (
  selectedQuestions: ReportQuestion[],
  templateId: string,
  getQuestionLabel: QuestionLabeller
): CsvColumn[] =>
  selectedQuestions.map(question => ({
    header: getQuestionLabel(question.id),
    getValue: (row: CandidateReportRow) => answerValue(getTemplateSubmissions(row, templateId)[0], question.id)
  }))

const buildPositionalMultipleSubmissionsColumns = (
  selectedQuestions: ReportQuestion[],
  templateId: string,
  submissionCount: number,
  getQuestionLabel: QuestionLabeller
): CsvColumn[] =>
  Array.from({ length: submissionCount }, (_, submissionIndex) =>
    selectedQuestions.map(
      (question): CsvColumn => ({
        header: `${getQuestionLabel(question.id)} [${submissionIndex + 1}]`,
        getValue: (row: CandidateReportRow) =>
          answerValue(getTemplateSubmissions(row, templateId)[submissionIndex], question.id)
      })
    )
  ).flat()

// Builds the question columns for a single task template, choosing how to lay submissions out:
//   • ≤ 1 submission per candidate → one plain column per question
//   • > 1 submission per candidate → positional [N] columns
const buildTemplateColumns = (
  template: TaskTemplate,
  reportData: CandidateReportRow[],
  questionIds: string[],
  getQuestionLabel: QuestionLabeller
): CsvColumn[] => {
  const templateId = template.taskTemplate.id
  const selectedQuestions = template.questions.filter(question => questionIds.includes(question.id))

  if (selectedQuestions.length === 0) {
    return []
  }

  const maxSubmissions = reportData.reduce(
    (max, row) => Math.max(max, getTemplateSubmissions(row, templateId).length),
    0
  )

  if (maxSubmissions <= 1) {
    return buildSingleSubmissionColumns(selectedQuestions, templateId, getQuestionLabel)
  }

  return buildPositionalMultipleSubmissionsColumns(selectedQuestions, templateId, maxSubmissions, getQuestionLabel)
}

const createSimpleColumns = (values: string[], getLabelFn: (value: string) => string): CsvColumn[] =>
  values.map(value => ({
    header: getLabelFn(value),
    getValue: row => get(row, value) ?? ''
  }))

export const buildCandidatesReportCsv = (
  reportData: CandidateReportRow[],
  { taskTemplates, questionIds, candidateDetails, disbursements }: BuildReportCsvParams
): Blob => {
  const questionsInHeader =
    taskTemplates?.flatMap(template =>
      template.questions.map(question => ({ id: question.id, displayName: question.dashboardLabel ?? question.label }))
    ) ?? []

  const getCandidateDetailLabel = (value: string) =>
    CANDIDATE_DETAILS.find(detail => detail.value === value)?.label ?? value
  const getTaskTemplateQuestionLabel = (id: string) =>
    questionsInHeader.find(question => question.id === id)?.displayName ?? id
  const getDisbursementDetailLabel = (value: string) =>
    DISBURSEMENTS.find(disbursement => disbursement.value === value)?.label ?? value

  const candidateDetailsColumns = createSimpleColumns(candidateDetails, getCandidateDetailLabel)

  const questionsFromTasksColumns = (taskTemplates ?? []).flatMap(template =>
    buildTemplateColumns(template, reportData, questionIds, getTaskTemplateQuestionLabel)
  )
  const disbursementColumns = createSimpleColumns(disbursements, getDisbursementDetailLabel)

  const columns: CsvColumn[] = [...candidateDetailsColumns, ...questionsFromTasksColumns, ...disbursementColumns]

  const headerRow = `${columns.map(column => escapeCsvValue(column.header)).join(',')}\r\n`
  const dataRows = reportData
    .map(row => columns.map(column => escapeCsvValue(column.getValue(row))).join(','))
    .join('\r\n')

  return new Blob([headerRow, dataRows], { type: 'text/csv;charset=utf-8;' })
}
